import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { SavedSociety, SocietyInput, SocietyResult } from '@creanizer/types'
import { createIndexedDBStorage } from '@creanizer/utils'

interface SocietyStore {
  savedSocieties: SavedSociety[]
  maxSocieties: number
  addSociety: (society: Omit<SavedSociety, 'id' | 'savedAt'>) => void
  removeSociety: (id: string) => void
  getSociety: (id: string) => SavedSociety | undefined
  updateSociety: (id: string, updates: Partial<SavedSociety>) => void
  clearAllSocieties: () => void
  exportSocieties: () => SavedSociety[]
  importSocieties: (societies: SavedSociety[]) => void
  exportAsText: () => void
  exportAsJSON: () => void
  importFromJSON: (jsonString: string) => void
  exportSingleAsText: (id: string) => void
  exportSingleAsJSON: (id: string) => void
}

export const useSocietyStore = create<SocietyStore>()(
  persist(
    (set, get) => ({
      savedSocieties: [],
      maxSocieties: 200,
      
      addSociety: (society) => {
        const newSociety: SavedSociety = {
          ...society,
          id: `society_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
          createdAt: society.createdAt || new Date().toISOString(),
          updatedAt: society.updatedAt || new Date().toISOString(),
        }
        
        set((state) => {
          const societies = [newSociety, ...state.savedSocieties]
          
          // 最大数を超えた場合は古いものから削除
          if (societies.length > state.maxSocieties) {
            societies.splice(state.maxSocieties)
          }
          
          return { savedSocieties: societies }
        })
      },
      
      removeSociety: (id) => {
        set((state) => ({
          savedSocieties: state.savedSocieties.filter((s) => s.id !== id),
        }))
      },
      
      getSociety: (id) => {
        return get().savedSocieties.find((s) => s.id === id)
      },
      
      updateSociety: (id, updates) => {
        set((state) => ({
          savedSocieties: state.savedSocieties.map((s) =>
            s.id === id ? { ...s, ...updates } : s
          ),
        }))
      },
      
      clearAllSocieties: () => {
        set({ savedSocieties: [] })
      },
      
      exportSocieties: () => {
        return get().savedSocieties
      },
      
      importSocieties: (societies) => {
        set((state) => {
          const existingIds = new Set(state.savedSocieties.map(s => s.id))
          const newSocieties = societies.filter(s => !existingIds.has(s.id))
          const combined = [...state.savedSocieties, ...newSocieties]
          
          // 最大数を超えた場合は古いものから削除
          if (combined.length > state.maxSocieties) {
            combined.splice(state.maxSocieties)
          }
          
          return { savedSocieties: combined }
        })
      },
      
      exportAsText: () => {
        const { savedSocieties } = get()
        if (savedSocieties.length === 0) {
          alert('保存された組織がありません。')
          return
        }
        
        const content = formatSocietiesForExport(savedSocieties)
        downloadTextFile(content, `saved_societies_${Date.now()}.txt`)
      },
      
      exportAsJSON: () => {
        const { savedSocieties } = get()
        if (savedSocieties.length === 0) {
          alert('保存された組織がありません。')
          return
        }
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'societies',
          data: savedSocieties
        }
        
        downloadJSONFile(exportData, `saved_societies_${Date.now()}.json`)
      },
      
      importFromJSON: (jsonString) => {
        try {
          console.log('[SocietyStore] importFromJSON開始')
          console.log('[SocietyStore] 受信JSON長さ:', jsonString.length)
          const importData = JSON.parse(jsonString)
          console.log('[SocietyStore] パース後データ:', importData)
          
          if (!importData.data || !Array.isArray(importData.data)) {
            console.error('[SocietyStore] データ形式エラー: dataプロパティが存在しないか配列ではありません')
            throw new Error('Invalid data format')
          }
          
          const societies = importData.data as SavedSociety[]
          console.log('[SocietyStore] インポートデータ数:', societies.length)
          const currentSocieties = get().savedSocieties
          const maxSocieties = get().maxSocieties
          console.log('[SocietyStore] 現在のデータ数:', currentSocieties.length)
          
          // データ検証：必要なフィールドが存在するか確認
          const validSocieties = societies.filter(society => {
            const isValid = society && society.result && society.result.basicInfo && society.result.basicInfo.name
            if (!isValid) {
              console.warn('[SocietyStore] 無効なデータ:', society)
            }
            return isValid
          })
          console.log('[SocietyStore] 有効なデータ数:', validSocieties.length)
          
          // 重複を除外
          const existingIds = new Set(currentSocieties.map(s => s.id))
          const existingNames = new Set(currentSocieties.map(s => s.result?.basicInfo?.name).filter(Boolean))
          
          const newSocieties = validSocieties.filter(society => {
            return !existingIds.has(society.id) && !existingNames.has(society.result.basicInfo.name)
          })
          
          // IDの重複を避けるため、新しいIDを生成して追加
          const importedSocieties = newSocieties.map((society, index) => ({
            ...society,
            id: `imported_society_${Date.now() + index}_${Math.random().toString(36).slice(2, 11)}`,
            createdAt: society.createdAt || new Date().toISOString(),
            updatedAt: society.updatedAt || new Date().toISOString()
          }))
          
          const availableSlots = maxSocieties - currentSocieties.length
          
          if (availableSlots <= 0) {
            alert(`保存可能な上限（${maxSocieties}件）に達しています。`)
            return
          }
          
          if (importedSocieties.length > availableSlots) {
            if (!confirm(`追加可能な件数は${availableSlots}件です。${importedSocieties.length}件中${availableSlots}件のみインポートしますか？`)) {
              return
            }
            importedSocieties.splice(availableSlots)
          }
          
          const mergedSocieties = [...importedSocieties, ...currentSocieties]
          console.log('[SocietyStore] マージ後データ数:', mergedSocieties.length)
          
          set({ savedSocieties: mergedSocieties })
          console.log('[SocietyStore] set完了')
          
          const skippedCount = societies.length - newSocieties.length
          if (importedSocieties.length > 0) {
            const message = `${importedSocieties.length}件の組織をインポートしました。${skippedCount > 0 ? `(${skippedCount}件の重複をスキップ)` : ''}`
            console.log('[SocietyStore] アラート表示:', message)
            alert(message)
          } else {
            console.log('[SocietyStore] インポートされたデータが0件')
          }
        } catch (error) {
          console.error('[SocietyStore] インポートエラー:', error)
          console.error('[SocietyStore] エラースタック:', (error as Error).stack)
          alert('インポートに失敗しました。ファイル形式を確認してください。')
          throw error
        }
      },
      
      exportSingleAsText: (id) => {
        const society = get().getSociety(id)
        if (!society) return
        
        const content = formatSingleSocietyForExport(society)
        downloadTextFile(content, `society_${society.result.basicInfo.name}_${Date.now()}.txt`)
      },
      
      exportSingleAsJSON: (id) => {
        const society = get().getSociety(id)
        if (!society) return
        
        const exportData = {
          version: '1.0.0',
          exportDate: new Date().toISOString(),
          dataType: 'society',
          data: society
        }
        
        downloadJSONFile(exportData, `society_${society.result.basicInfo.name}_${Date.now()}.json`)
      },
    }),
    {
      name: 'society-storage',
      version: 1,
      storage: createIndexedDBStorage('society-storage'),
      partialize: (state) => ({ 
        savedSocieties: state.savedSocieties,
        maxSocieties: state.maxSocieties
      })
    }
  )
)

// 単一の組織をテキスト形式にフォーマット
function formatSingleSocietyForExport(society: SavedSociety): string {
  const { result } = society
  const date = new Date(society.createdAt).toLocaleString('ja-JP')
  
  let content = `【組織情報】
`
  content += `生成日時: ${date}\n`
  content += '=' .repeat(50) + '\n\n'
  
  // 基本情報
  content += `基本情報:\n`
  content += `  名称: ${result.basicInfo.name}\n`
  if (result.basicInfo.aliases && result.basicInfo.aliases.length > 0) {
    content += `  別名: ${result.basicInfo.aliases.join('、')}\n`
  }
  content += `  種類: ${result.basicInfo.type}\n`
  content += `  規模: ${result.basicInfo.scale}\n`
  content += `  概要: ${result.basicInfo.summary}\n`
  if (result.basicInfo.purpose) {
    content += `  目的: ${result.basicInfo.purpose}\n`
  }
  if (result.basicInfo.motto) {
    content += `  モットー: 「${result.basicInfo.motto}」\n`
  }
  content += '\n'
  
  // 組織構造
  if (result.structure) {
    content += `組織構造:\n`
    if (result.structure.hierarchy?.length > 0) {
      content += `  階層構造:\n`
      result.structure.hierarchy.forEach(level => {
        content += `    - ${level.title}: ${level.responsibilities}\n`
      })
    }
    if (result.structure.departments?.length > 0) {
      content += `  部門:\n`
      result.structure.departments.forEach(dept => {
        content += `    - ${dept.name}: ${dept.purpose}\n`
      })
    }
    content += `  メンバー数: ${result.structure.memberCount}\n`
    if (result.structure.recruitmentMethod) {
      content += `  採用方法: ${result.structure.recruitmentMethod}\n`
    }
    content += '\n'
  }
  
  // メンバー
  if (result.members?.importantMembers?.length > 0) {
    content += `重要メンバー:\n`
    result.members.importantMembers.forEach((member, index) => {
      content += `  ${index + 1}. ${member.name} (${member.role})\n`
      if (member.description) {
        content += `     ${member.description}\n`
      }
    })
    if (result.members.notes) {
      content += `  備考: ${result.members.notes}\n`
    }
    content += '\n'
  }
  
  // 特徴
  if (result.features) {
    if (result.features.activities) {
      content += `活動内容:\n  ${result.features.activities}\n\n`
    }
    if (result.features.culture) {
      content += `組織文化:\n  ${result.features.culture}\n\n`
    }
    if (result.features.relations) {
      content += `他組織との関係:\n  ${result.features.relations}\n\n`
    }
    if (result.features.secrets) {
      content += `秘密・裏設定:\n  ${result.features.secrets}\n\n`
    }
  }
  
  // エピソード
  if (result.episodes?.episodes && result.episodes.episodes.length > 0) {
    content += `エピソード:\n`
    result.episodes.episodes.forEach((episode, index) => {
      content += `  ${index + 1}. ${episode.title}\n`
      if (episode.source) {
        content += `     出典: ${episode.source}\n`
      }
      if (episode.reliability) {
        content += `     信憑性: ${episode.reliability}\n`
      }
      if (episode.content) {
        content += `     ${episode.content}\n`
      }
      content += '\n'
    })
  }
  
  return content
}

// 組織をテキスト形式にフォーマット
function formatSocietiesForExport(societies: SavedSociety[]): string {
  const date = new Date().toLocaleString('ja-JP')
  
  let content = `【保存された組織一覧】
生成日時: ${date}
総数: ${societies.length}件

`
  
  societies.forEach((society, index) => {
    const { result } = society
    content += `\n【${index + 1}】 ${result.basicInfo.name}\n`
    content += '=' .repeat(50) + '\n\n'
    
    // 基本情報
    content += `基本情報:\n`
    content += `  名称: ${result.basicInfo.name}\n`
    content += `  種類: ${result.basicInfo.type}\n`
    content += `  規模: ${result.basicInfo.scale}\n`
    content += `  概要: ${result.basicInfo.summary}\n\n`
    
    // 組織構造
    if (result.structure) {
      content += `組織構造:\n`
      if (result.structure.hierarchy?.length > 0) {
        content += `  階層構造:\n`
        result.structure.hierarchy.slice(0, 3).forEach(level => {
          content += `    - ${level.title}: ${level.responsibilities}\n`
        })
      }
      if (result.structure.departments?.length > 0) {
        content += `  部門:\n`
        result.structure.departments.forEach(dept => {
          content += `    - ${dept.name}: ${dept.purpose}\n`
        })
      }
      content += `  メンバー数: ${result.structure.memberCount}\n`
      if (result.structure.recruitmentMethod) {
        content += `  採用方法: ${result.structure.recruitmentMethod}\n`
      }
      content += '\n'
    }
    
    // メンバー
    if (result.members?.importantMembers?.length > 0) {
      content += `重要メンバー:\n`
      result.members.importantMembers.forEach((member, index) => {
        content += `  ${index + 1}. ${member.name} (${member.role})\n`
      })
      if (result.members.notes) {
        content += `  備考: ${result.members.notes}\n`
      }
      content += '\n'
    }
    
    // 特徴
    if (result.features) {
      if (result.features.activities) {
        content += `活動内容:\n  ${result.features.activities}\n\n`
      }
      if (result.features.culture) {
        content += `組織文化:\n  ${result.features.culture}\n\n`
      }
      if (result.features.relations) {
        content += `他組織との関係:\n  ${result.features.relations}\n\n`
      }
      if (result.features.secrets) {
        content += `秘密・裏設定:\n  ${result.features.secrets}\n\n`
      }
    }
    
    content += '\n'
  })
  
  return content
}

// テキストファイルをダウンロード
function downloadTextFile(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// JSONファイルをダウンロード
function downloadJSONFile(data: any, filename: string) {
  const json = JSON.stringify(data, null, 2)
  const blob = new Blob([json], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = filename
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}